import React from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Base input classes
    const baseInputClasses = "flex h-12 w-full rounded-lg border-2 border-cosmic-gold/30 bg-cosmic-midnight/50 backdrop-blur-sm px-4 py-3 text-sm text-cosmic-lightGold ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-cosmic-lightGold/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cosmic-gold focus-visible:ring-offset-2 focus-visible:border-cosmic-gold disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300";

    // Checkbox-specific styles
    if (type === "checkbox") {
        return (
            <input
                type="checkbox"
                className={cn(
                    "h-5 w-5 rounded border-2 border-cosmic-gold/30 bg-cosmic-midnight/50 text-cosmic-gold focus:ring-2 focus:ring-cosmic-gold focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 cursor-pointer",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // Radio button-specific styles
    if (type === "radio") {
        return (
            <input
                type="radio"
                className={cn(
                    "h-5 w-5 rounded-full border-2 border-cosmic-gold/30 bg-cosmic-midnight/50 text-cosmic-gold focus:ring-2 focus:ring-cosmic-gold focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 cursor-pointer",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // For regular inputs with wrapper structure
    return (
        <div className="space-y-2">
            {label && (
                <label
                    htmlFor={inputId}
                    className={cn(
                        "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 font-thai text-cosmic-lightGold",
                        error ? "text-destructive" : ""
                    )}
                >
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                </label>
            )}

            <input
                type={type}
                className={cn(
                    baseInputClasses,
                    error && "border-destructive focus-visible:ring-destructive",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />

            {description && !error && (
                <p className="text-sm text-cosmic-lightGold/60">
                    {description}
                </p>
            )}

            {error && (
                <p className="text-sm text-destructive">
                    {error}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;